import React, { useId, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const MONTH_LABELS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

const MONTH_SHORT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
] as const;

const COMPETENCE = /^(\d{4})-(0[1-9]|1[0-2])$/;

function parseCompetence(value: string): { year: number; month: number } | null {
  const match = COMPETENCE.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) };
}

function formatCompetenceLabel(value: string): string {
  const parsed = parseCompetence(value);
  if (!parsed) return value || '';
  return `${MONTH_LABELS[parsed.month - 1]} de ${parsed.year}`;
}

function toCompetence(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function currentBrazilParts(): { year: number; month: number } {
  const iso = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  return { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) };
}

export interface CompetenceMonthFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  required?: boolean;
  helperText?: string;
  disabled?: boolean;
  allowEmpty?: boolean;
  /** Largura mínima do campo fechado. */
  minWidth?: number | string;
}

/**
 * Seletor de competência AAAA-MM.
 * O campo inteiro abre a agenda; a grade de meses substitui o type=month nativo.
 */
const CompetenceMonthField: React.FC<CompetenceMonthFieldProps> = ({
  label = 'Competência',
  value,
  onChange,
  size = 'medium',
  fullWidth = false,
  required = false,
  helperText,
  disabled = false,
  allowEmpty = false,
  minWidth = 240,
}) => {
  const labelId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const parsed = parseCompetence(value);
  const today = useMemo(() => currentBrazilParts(), []);
  const [viewYear, setViewYear] = useState(() => parsed?.year ?? today.year);

  const open = Boolean(anchor);

  const openPicker = (target: HTMLElement) => {
    if (disabled) return;
    setViewYear(parsed?.year ?? today.year);
    setAnchor(target);
  };

  const closePicker = () => setAnchor(null);

  const selectMonth = (month: number) => {
    onChange(toCompetence(viewYear, month));
    closePicker();
  };

  const display = parsed ? formatCompetenceLabel(value) : allowEmpty && !value ? '' : value;

  return (
    <>
      <TextField
        label={label}
        value={display}
        required={required}
        disabled={disabled}
        size={size}
        fullWidth={fullWidth}
        helperText={helperText}
        onClick={(event: React.MouseEvent<HTMLElement>) => openPicker(event.currentTarget)}
        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
          if (disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker(event.currentTarget);
          }
        }}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <CalendarMonthOutlinedIcon fontSize="small" color={disabled ? 'disabled' : 'action'} />
              </InputAdornment>
            ),
          },
          inputLabel: { shrink: true, id: labelId },
          htmlInput: {
            'aria-labelledby': labelId,
            'aria-haspopup': 'dialog',
            'aria-expanded': open,
            role: 'combobox',
            style: { cursor: disabled ? 'default' : 'pointer' },
          },
        }}
        sx={{
          minWidth,
          cursor: disabled ? 'default' : 'pointer',
          '& .MuiInputBase-root': { cursor: disabled ? 'default' : 'pointer' },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              p: 1.5,
              width: 300,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)',
            },
          },
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <IconButton
              size="small"
              aria-label="Ano anterior"
              onClick={() => setViewYear((year) => year - 1)}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {viewYear}
            </Typography>
            <IconButton
              size="small"
              aria-label="Próximo ano"
              onClick={() => setViewYear((year) => year + 1)}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 0.75,
            }}
          >
            {MONTH_SHORT.map((short, index) => {
              const month = index + 1;
              const selected = parsed?.year === viewYear && parsed.month === month;
              const isCurrent = today.year === viewYear && today.month === month;
              return (
                <Button
                  key={short}
                  size="small"
                  variant={selected ? 'contained' : 'text'}
                  color={selected ? 'primary' : 'inherit'}
                  onClick={() => selectMonth(month)}
                  aria-pressed={selected}
                  sx={{
                    minWidth: 0,
                    py: 1.1,
                    borderRadius: 1.5,
                    fontWeight: selected ? 800 : 600,
                    textTransform: 'none',
                    border: isCurrent && !selected ? '1px solid' : '1px solid transparent',
                    borderColor: isCurrent && !selected ? 'primary.light' : 'transparent',
                    bgcolor: selected ? undefined : isCurrent ? 'action.hover' : 'transparent',
                    '&:hover': {
                      bgcolor: selected ? undefined : 'action.selected',
                    },
                  }}
                >
                  {short}
                </Button>
              );
            })}
          </Box>

          {allowEmpty ? (
            <Button
              size="small"
              onClick={() => {
                onChange('');
                closePicker();
              }}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
            >
              Limpar
            </Button>
          ) : null}
        </Stack>
      </Popover>
    </>
  );
};

export default CompetenceMonthField;
export { formatCompetenceLabel, parseCompetence, toCompetence };
